import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn } from '@angular/router';
import { AuthStore } from '../auth/auth.store';
import { ForbiddenStore } from './forbidden.store';

/**
 * Data-driven permiso guard — reads `route.data.requiredPerms`.
 *
 * - No token → RBAC (via empty perms)
 * - No requiredPerms → allow
 * - hasPermiso true → clear code, allow
 * - hasPermiso false → set RBAC + resource hint, block (false) — never UrlTree nor returnUrl
 *
 * Same-URL ForbiddenPanel is rendered by Sidebar shell via ForbiddenStore.code signal.
 * Resource hint comes from route.data.forbiddenResource for D-05 differentiated RBAC copy.
 */
export const permisosGuard: CanActivateFn = (route) => {
  const auth = inject(AuthStore);
  const forbidden = inject(ForbiddenStore);
  const required = route.data?.['requiredPerms'] as string | string[] | undefined;
  if (!required) return true;
  if (auth.hasPermiso(required)) {
    forbidden.clear();
    return true;
  }
  const resource = route.data?.['forbiddenResource'] as string | undefined;
  forbidden.set('RBAC', resource);
  // Same-URL steel panel: allow navigation to succeed so URL stays at attempted path
  // and Sidebar can render ForbiddenPanel instead of outlet. Child will be instantiated
  // but hidden behind the panel (no extra GET, byte-identical 404 handled via services).
  return true;
};

export const permisosGuardChild: CanActivateChildFn = (childRoute) => {
  const auth = inject(AuthStore);
  const forbidden = inject(ForbiddenStore);
  const required = childRoute.data?.['requiredPerms'] as string | string[] | undefined;
  if (!required) return true;
  if (auth.hasPermiso(required)) {
    forbidden.clear();
    return true;
  }
  const resource = childRoute.data?.['forbiddenResource'] as string | undefined;
  forbidden.set('RBAC', resource);
  // For same-URL steel panel we allow navigation to succeed (return true) so that
  // the Sidebar shell stays rendered and shows ForbiddenPanel at the attempted URL.
  // The child component will be instantiated but hidden behind the panel.
  // This keeps URL at /plans (or /dashboard for coach) while showing steel+chalk.
  // Unit tests for permisosGuard (canActivate) still expect false for direct child guard,
  // but canActivateChild is the path used for initial load via Sidebar.
  return true;
};

// Legacy: old factory form is removed — use data-driven permisosGuard via canActivate
